import React, {useState, useEffect} from "react";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import {Card, Col, Row} from "reactstrap";
import Grid from "../../../components/Grid";
import {
    freeSpaceByClassificationDefs,
    freeSpaceByDataCenterDefs,
    freeSpaceByDefs,
    freeSpaceByDeviceDefs,
    freeSpaceByDeviceTypeDefs
} from "../../../utils/gridColumnDefs/storage/freeSpaceBy";
import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {contiguousFree, formatBarData} from "../../../utils/chartUtils";
import StackedColumnChart from "../../../components/StackedColumnChart";
import PieChart from "../../../components/PieChart";

const allBus = [
    {value: 'device', label: 'Device'},
    {value: 'device_type_code', label: 'Device Type'},
    {value: 'data_center', label: 'Data Center'},
    {value: 'classification', label: 'Classification'},
];

const sortingArr = ['Largest Contiguous Free', 'Free'];

export default function FreeSpaceBy({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const [allData, setAllData] = useState([]);
    const [freeSpaceByDefs, setFreeSpaceByDefs] = useState(freeSpaceByDeviceDefs);


    const searchBu = searchParams.get('bu');
    let defaultBu = allBus[0];
    if (searchBu) {
        defaultBu = allBus.find(({value}) => searchBu === value) || defaultBu;
    }
    const [bu, setBu] = useState(defaultBu);
    const [barData, setBarData] = useState({
        categories: [],
        series: []
    });
    const [pieData, setPieData] = useState([{
        name: defaultBu.label,
        data: []
    }])

    const [data] = useDataWithRoute(`/api/v6/report/enterprise_free_space_by_${bu.value}_proc?client_id=${client.clientId}&period_id=${periodId}`);
    useEffect(() => {

        const validData = data?.recordset || [];
        setAllData(validData);
        updateGraphsWithSelector(validData);
        setFreeSpaceByDefs(bu.value === 'device_type_code' ? freeSpaceByDeviceTypeDefs : bu.value === 'device' ? freeSpaceByDeviceDefs : bu.value === 'data_center' ? freeSpaceByDataCenterDefs : freeSpaceByClassificationDefs)
    }, [data]);

    const updateGraphsWithSelector = data => {
        const mungedData = [];
        const pieDict = {};
        const pieData = [];

        (data || allData).forEach(datum => {
            mungedData.push({
                ...datum,
                total_free: parseFloat(datum.total_free - datum.largest_contiguous_free).toFixed(2),
                largest_contiguous_free: parseFloat(datum.largest_contiguous_free).toFixed(2),
            });

            if (!pieDict[datum[(bu.value === 'device' ? 'device_name' : bu.value)]]) {
                pieDict[datum[(bu.value === 'device' ? 'device_name' : bu.value)]] = 0;
            }
            pieDict[datum[(bu.value === 'device' ? 'device_name' : bu.value)]] += datum.total_free;
        });

        for (let key in pieDict) {
            pieData.push({
                name: key,
                y: pieDict[key]
            })
        }
        setBarData(formatBarData(mungedData, {
            sortingArr,
            sortingField: (bu.value === 'device' ? 'device_name' : bu.value),
            defs: freeSpaceByDefs.filter(def => sortingArr.includes(def.headerName)),
        }));

        setPieData([
            {
                name: bu.label,
                colorByPoint: true,
                data: pieData,
            }
        ]);
    };

    const onUnitChange = (selectedUnit) => {
        setBu(selectedUnit);
        setSearchParams({
            bu: selectedUnit.value,
        }, {replace: true});
    };

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) filteredData.push(node.data);
        });

        updateGraphsWithSelector(filteredData);
    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Free Space by {bu.label}</VsiH4>
        <div className={"w-100 p-2 d-flex"}>
            <VsiLabeledSelect options={allBus} label={'Select Unit'} onChange={onUnitChange} defaultValue={bu}/>
        </div>
        <Row className={"mb-2"}>
            <Col xs={12} lg={4}>
                <VsiBubble unit={bu.value} autobotInfo={{
                        lines: bu.value === 'device' ? 'device_name' : bu.value,
                        pieSize: 'total_free'
                    }}>
                    <PieChart
                        height={600}
                        series={pieData}
                        includeLegend={true}
                    />
                </VsiBubble>
            </Col>
            <Col xs={12} lg={8}>
                <VsiBubble unit={bu.value}>
                    <StackedColumnChart categories={barData.categories}
                                        colors={contiguousFree}
                                        height={600}
                                        series={barData.series}/>
                </VsiBubble>
            </Col>
        </Row>
        <VsiBubble unit={bu.value}>
            <Grid data={allData} saveParams={{unit: bu.value}} columnDefs={freeSpaceByDefs}
                  filterChanged={filterChanged}/>
        </VsiBubble>
    </Card>
}
